<template>
  <div class="dd-cell" @click="$emit('click', cell)">
    <plotly-chart
      v-if="traces.length > 0"
      :data="traces"
      :layout="layout"
      :display-mode-bar="false"
    />
    <div v-else class="dd-cell-empty">no data</div>

    <div v-if="stats" :class="['dd-cell-p', { 'dd-sig': significant }]">
      {{ pValueLabel }}
    </div>
  </div>
</template>

<script>
import { boxTraces, formatPValue, stackedBarTraces } from '@/utils/dashboard';
import PlotlyChart from '@/components/common/PlotlyChart.vue';

export default {
  name: 'MatrixCell',
  components: {
    PlotlyChart,
  },
  props: {
    // one entry from the parent's matrix: {continuous, levels, stats, tab, ...}
    cell: {
      type: Object,
      required: true,
    },
    // y-range shared by every cell in this outcome's row; null when unusable
    rowRange: {
      type: Array,
      default: null,
    },
    // computed statistics, present only once the drill-down has been opened
    stats: {
      type: Object,
      default: null,
    },
    height: {
      type: Number,
      default: 180,
    },
  },
  computed: {
    traces() {
      return this.cell.continuous
        ? boxTraces(this.cell.levels, this.cell.stats)
        : stackedBarTraces(this.cell.tab);
    },
    // axis titles and legends live in the row and column headers instead, so
    // the cell spends its pixels on the data
    layout() {
      return {
        height: this.height,
        autosize: true,
        showlegend: false,
        barmode: 'stack',
        // a hover box sized for a full plot covers most of a cell this small,
        // and leaving it off matches the sparklines in the summary above. The
        // numbers are a click away in the drill-down.
        hovermode: false,
        // the top margin is wide enough to seat the p-value overlay
        margin: { t: 16, r: 8, b: 34, l: 44 },
        yaxis: {
          range: this.yRange,
          tickfont: { size: 9 },
          zeroline: false,
          automargin: false,
          fixedrange: true,
        },
        xaxis: {
          type: 'category',
          tickfont: { size: 9 },
          tickangle: -30,
          automargin: false,
          fixedrange: true,
        },
        font: { family: 'Roboto, sans-serif', size: 10 },
      };
    },
    // stacked bars are counts and always start at zero, so only the box rows
    // take the shared range
    yRange() {
      if (!this.cell.continuous) return undefined;
      return this.rowRange === null ? undefined : this.rowRange;
    },
    pValueLabel() {
      return this.stats ? formatPValue(this.stats.pval) : '';
    },
    significant() {
      return !!this.stats && this.stats.pval != null && this.stats.pval < 0.05;
    },
  },
};
</script>

<style scoped>
.dd-cell {
  cursor: pointer;
  position: relative;
}
.dd-cell:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
.dd-cell-empty {
  font-size: 11px;
  color: #999;
  font-style: italic;
  text-align: center;
  padding: 70px 0;
}
/* floats over the plot so the cell spends none of its height on it; only
   appears for a pair whose drill-down has already been opened */
.dd-cell-p {
  position: absolute;
  top: 2px;
  right: 2px;
  line-height: 1;
  font-family: monospace;
  font-size: 10px;
  color: #777;
}
.dd-sig {
  font-weight: 700;
  color: #c62828;
}
</style>
